// Service to manage recently viewed cars in localStorage

const STORAGE_KEY = 'recentlyViewedCars';
const MAX_ITEMS = 20; // Maximum number of recently viewed cars to keep

/**
 * Get all recently viewed cars
 * @returns {Array} Array of car objects with timestamp
 */
export const getRecentlyViewedCars = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch (error) {
    console.error('Error loading recently viewed cars:', error);
  }
  return [];
};

/**
 * Add a car to recently viewed list
 * @param {Object} car - Car object to add
 */
export const addToRecentlyViewed = (car) => {
  if (!car || !car.id) {
    console.warn('Invalid car object provided to addToRecentlyViewed');
    return;
  }

  try {
    let recentlyViewed = getRecentlyViewedCars();
    
    // Remove if already exists (to avoid duplicates)
    recentlyViewed = recentlyViewed.filter(item => String(item.car.id) !== String(car.id));
    
    // Add new car with timestamp at the beginning
    recentlyViewed.unshift({
      car: {
        id: car.id,
        stockNumber: car.stockNumber,
        year: car.year,
        make: car.make,
        model: car.model,
        price: car.price,
        images: car.images,
        mileage: car.mileage,
        fuelType: car.fuelType,
        transmission: car.transmission,
        bodyType: car.bodyType, // Added for recommendations
      },
      viewedAt: new Date().toISOString(),
    });
    
    // Keep only the most recent MAX_ITEMS
    if (recentlyViewed.length > MAX_ITEMS) {
      recentlyViewed = recentlyViewed.slice(0, MAX_ITEMS);
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(recentlyViewed));
    
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('recentlyViewedUpdated'));
  } catch (error) {
    console.error('Error saving recently viewed car:', error);
  }
};

/**
 * Clear all recently viewed cars
 */
export const clearRecentlyViewed = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('recentlyViewedUpdated'));
  } catch (error) {
    console.error('Error clearing recently viewed cars:', error);
  }
};

/**
 * Remove a specific car from recently viewed
 * @param {string|number} carId - ID of the car to remove
 */
export const removeFromRecentlyViewed = (carId) => {
  try {
    let recentlyViewed = getRecentlyViewedCars();
    recentlyViewed = recentlyViewed.filter(item => String(item.car.id) !== String(carId));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(recentlyViewed));
  } catch (error) {
    console.error('Error removing car from recently viewed:', error);
  }
};

/**
 * Get recently viewed cars with formatted date
 * @param {number} limit - Maximum number of cars to return
 * @returns {Array} Array of car objects with formatted viewed date
 */
export const getFormattedRecentlyViewed = (limit = 10) => {
  const recentlyViewed = getRecentlyViewedCars();
  
  return recentlyViewed.slice(0, limit).map(item => ({
    ...item.car,
    viewedAt: item.viewedAt,
    viewedDate: formatViewDate(item.viewedAt),
  }));
};

/**
 * Format the viewed date for display
 * @param {string} isoDate - ISO date string
 * @returns {string} Formatted date string
 */
const formatViewDate = (isoDate) => {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) {
    return 'Just now';
  } else if (diffMins < 60) {
    return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  }
};

